/**
 * Contact Offline Service
 *
 * Extends BaseOfflineService for contact-specific operations.
 */

import type { Table } from 'dexie';

import { offlineDb } from '../database/offlineDatabase';
import { BaseOfflineService } from './BaseOfflineService';
import type {
  EntityType,
  OfflineEntity,
  ApiServiceMethods,
} from '../types/offline.types';

// Import contact types
import type {
  Contact,
  CreateContactDto,
  UpdateContactDto,
  QueryContactsDto,
} from '@/types/contacts.types';
import type { PaginatedResponse } from '#/api';
import { ContactType } from '@/types/enum';

// Import online service
import * as contactService from '@/api/services/contactService';

// ============================================
// OFFLINE SERVICE
// ============================================

export class ContactOfflineService extends BaseOfflineService<
  Contact,
  CreateContactDto,
  UpdateContactDto
> {
  protected readonly entityType: EntityType = 'contact';
  protected readonly table: Table<OfflineEntity<Contact>, string> = offlineDb.contacts;

  protected readonly apiService: ApiServiceMethods<
    Contact,
    CreateContactDto,
    UpdateContactDto
  > = {
    getAll: (filters) => contactService.getAll(filters as QueryContactsDto),
    getOne: contactService.getOne,
    create: contactService.create,
    update: contactService.update,
    delete: contactService.deleteContact,
  };

  // ============================================
  // CONTACT-SPECIFIC METHODS
  // ============================================

  /**
   * Get contacts by type (SUPPLIER, CUSTOMER, BOTH, OTHER)
   */
  async getByType(type: ContactType): Promise<Contact[]> {
    const contacts = await this.table.where('type').equals(type).toArray();
    return contacts.map(this.stripOfflineMetadata);
  }

  /**
   * Get suppliers (SUPPLIER or BOTH)
   */
  async getSuppliers(): Promise<Contact[]> {
    const contacts = await this.table
      .filter((c) => c.type === ContactType.SUPPLIER || c.type === ContactType.BOTH)
      .toArray();
    return contacts.map(this.stripOfflineMetadata);
  }

  /**
   * Get customers (CUSTOMER or BOTH)
   */
  async getCustomers(): Promise<Contact[]> {
    const contacts = await this.table
      .filter((c) => c.type === ContactType.CUSTOMER || c.type === ContactType.BOTH)
      .toArray();
    return contacts.map(this.stripOfflineMetadata);
  }

  /**
   * Search contacts by name
   */
  async searchByName(searchTerm: string): Promise<Contact[]> {
    const term = searchTerm.toLowerCase();
    const contacts = await this.table
      .filter((c) => c.name.toLowerCase().includes(term))
      .toArray();
    return contacts.map(this.stripOfflineMetadata);
  }

  /**
   * Override applyFilters for contact-specific filtering
   */
  protected override applyFilters(
    items: OfflineEntity<Contact>[],
    filters: Record<string, unknown>
  ): OfflineEntity<Contact>[] {
    return items.filter((item) => {
      // Skip deleted items
      if (item.isDeleted) {
        return false;
      }

      // Type filter
      if (filters.type && item.type !== filters.type) {
        return false;
      }

      // Branch filter
      if (filters.branchId && item.branchId !== filters.branchId) {
        return false;
      }

      // Search filter
      if (filters.search) {
        const searchTerm = (filters.search as string).toLowerCase();
        const name = item.name.toLowerCase();
        const phone = item.phone?.toLowerCase() ?? '';
        const email = item.email?.toLowerCase() ?? '';
        if (
          !name.includes(searchTerm) &&
          !phone.includes(searchTerm) &&
          !email.includes(searchTerm)
        ) {
          return false;
        }
      }

      return true;
    });
  }
}

// ============================================
// SINGLETON INSTANCE
// ============================================

export const contactOfflineService = new ContactOfflineService();
